import type { Branch } from '../auth/types';
import type {
  CatalogSnapshot,
  ClosureIssue,
  ClosureScope,
  ClosureSummary,
  OperationsSnapshot,
  PaymentMethod,
  Phase8Snapshot,
  ReceivableEntry,
  SalesSnapshot,
  SupplierPayableEntry,
} from '../../types/domain';

const PAYMENT_METHODS: PaymentMethod[] = ['cash', 'transfer', 'card', 'deposit', 'credit', 'other'];
const dateOf = (value?: string) => value?.slice(0, 10) ?? '';
const inRange = (value: string | undefined, from: string, to: string) => {
  const date = dateOf(value);
  return Boolean(date && date >= from && date <= to);
};
const emptyMethods = () => Object.fromEntries(PAYMENT_METHODS.map((method) => [method, 0])) as Record<PaymentMethod, number>;
const issueId = (code: string, entityId = '') => `issue_${code}_${entityId || Math.random().toString(36).slice(2)}`;

export type ClosureBuildInput = {
  businessId: string;
  branchId?: string;
  scope: ClosureScope;
  from: string;
  to: string;
  branches: Branch[];
  sales: SalesSnapshot;
  catalog: CatalogSnapshot;
  operations: OperationsSnapshot;
  phase8: Phase8Snapshot;
  receivables: ReceivableEntry[];
  payables: SupplierPayableEntry[];
};

export function buildClosureSummary(input: ClosureBuildInput): ClosureSummary {
  const { sales, catalog, operations, phase8, branchId, from, to } = input;
  const branchMatches = (value?: string) => !branchId || value === branchId;
  const periodSales = sales.sales.filter((sale) => branchMatches(sale.branchId) && inRange(sale.createdAt, from, to) && sale.status !== 'cancelled');
  const periodExpenses = operations.expenses.filter((expense) => branchMatches(expense.branchId) && expense.status === 'recorded' && inRange(expense.expenseDate, from, to));
  const periodMovements = sales.cashMovements.filter((movement) => branchMatches(movement.branchId) && inRange(movement.createdAt, from, to));
  const periodMembershipCharges = phase8.membershipCharges.filter((charge) => charge.paidAt && inRange(charge.paidAt, from, to));
  const periodInventory = catalog.movements.filter((movement) => branchMatches(movement.branchId) && inRange(movement.createdAt, from, to));
  const periodSessions = sales.cashSessions.filter((session) => branchMatches(session.branchId) && (inRange(session.openedAt, from, to) || inRange(session.closedAt, from, to)));
  const paymentMethods = emptyMethods();
  const channels: Record<string, number> = {};
  let estimatedGrossProfit = 0;
  const topMap = new Map<string, { productId: string; name: string; quantity: number; revenueCents: number }>();
  for (const sale of periodSales) {
    channels[sale.channel] = (channels[sale.channel] ?? 0) + sale.totalCents;
    for (const payment of sale.payments) paymentMethods[payment.method] += payment.amountCents;
    for (const item of sale.items) {
      if (item.costCents > 0 || item.supplyType === 'service') estimatedGrossProfit += (item.unitPriceCents - item.costCents) * item.quantity;
      const current = topMap.get(item.productId) ?? { productId: item.productId, name: item.name, quantity: 0, revenueCents: 0 };
      current.quantity += item.quantity;
      current.revenueCents += item.unitPriceCents * item.quantity;
      topMap.set(item.productId, current);
    }
  }
  for (const charge of periodMembershipCharges) paymentMethods[charge.paymentMethod ?? 'other'] += charge.paidCents;
  const cashSessionsOpen = sales.cashSessions.filter((session) => branchMatches(session.branchId) && ['open', 'paused'].includes(session.status) && dateOf(session.openedAt) <= to).length;
  const cashSessionsClosed = periodSessions.filter((session) => ['closed', 'difference'].includes(session.status)).length;
  const cashSessionsWithDifference = periodSessions.filter((session) => (session.differenceCents ?? 0) !== 0 || session.status === 'difference').length;
  const sessionExpected = periodSessions.reduce((sum, session) => sum + (session.expectedCents ?? 0), 0);
  const sessionCounted = periodSessions.reduce((sum, session) => sum + (session.countedCents ?? 0), 0);
  const cashExpectedCents = sessionExpected || periodMovements.filter((movement) => movement.method === 'cash').reduce((sum, movement) => sum + (movement.direction === 'in' ? movement.amountCents : -movement.amountCents), 0) + periodSessions.reduce((sum, session) => sum + session.openingFloatCents, 0);
  const cashCountedCents = sessionCounted || Math.max(0, cashExpectedCents);
  const pendingOrders = sales.publicOrders.filter((order) => branchMatches(order.branchId) && !['completed', 'cancelled'].includes(order.status) && dateOf(order.createdAt) <= to).length;
  const pendingSpecialOrders = sales.sales.filter((sale) => branchMatches(sale.branchId) && ['reserved', 'pending-order', 'ready', 'partially-delivered'].includes(sale.status) && dateOf(sale.createdAt) <= to).length;
  const advancesCents = periodSales.filter((sale) => sale.mode !== 'sale').reduce((sum, sale) => sum + Math.min(sale.paidCents, sale.totalCents), 0);
  const membershipCollectedCents = periodMembershipCharges.reduce((sum, charge) => sum + charge.paidCents, 0);
  const expenseCents = periodExpenses.reduce((sum, expense) => sum + expense.amountCents, 0);
  const withdrawalsCents = periodMovements.filter((movement) => movement.type === 'withdrawal' && movement.direction === 'out').reduce((sum, movement) => sum + movement.amountCents, 0);
  const extraordinaryIncomeCents = periodMovements.filter((movement) => movement.type === 'income' && movement.direction === 'in').reduce((sum, movement) => sum + movement.amountCents, 0);
  const refundsCents = periodMovements.filter((movement) => movement.type === 'refund' && movement.direction === 'out').reduce((sum, movement) => sum + movement.amountCents, 0);
  const salesCents = periodSales.reduce((sum, sale) => sum + sale.totalCents, 0);
  const collectedCents = periodSales.reduce((sum, sale) => sum + Math.min(sale.paidCents, sale.totalCents), 0) + membershipCollectedCents;
  return {
    salesCents,
    collectedCents,
    creditCents: periodSales.reduce((sum, sale) => sum + sale.balanceCents, 0),
    advancesCents,
    membershipCollectedCents,
    expenseCents,
    withdrawalsCents,
    extraordinaryIncomeCents,
    refundsCents,
    estimatedProfitCents: estimatedGrossProfit - expenseCents,
    cashExpectedCents,
    cashCountedCents,
    cashDifferenceCents: cashCountedCents - cashExpectedCents,
    tickets: periodSales.length,
    averageTicketCents: periodSales.length ? Math.round(salesCents / periodSales.length) : 0,
    newCustomers: sales.customers.filter((customer) => inRange(customer.createdAt, from, to)).length,
    pendingOrders,
    pendingSpecialOrders,
    openCashSessions: cashSessionsOpen,
    cashSessionsClosed,
    cashSessionsWithDifference,
    inventoryAdjustments: periodInventory.filter((movement) => ['adjustment', 'count', 'manual-in', 'manual-out'].includes(movement.type)).length,
    wasteMovements: periodInventory.filter((movement) => ['waste', 'loss'].includes(movement.type)).length,
    unsyncedOperations: 0,
    paymentMethods,
    channels,
    topProducts: [...topMap.values()].sort((a, b) => b.revenueCents - a.revenueCents).slice(0, 8),
  };
}

export function buildClosureIssues(input: ClosureBuildInput, closureId: string, summary: ClosureSummary): ClosureIssue[] {
  const now = new Date().toISOString();
  const issues: ClosureIssue[] = [];
  const push = (code: string, severity: ClosureIssue['severity'], blocking: boolean, title: string, detail: string, entityType?: string, entityId?: string) => issues.push({ id: issueId(code, entityId), businessId: input.businessId, closureId, branchId: input.branchId, code, severity, blocking, title, detail, entityType, entityId, resolved: false, createdAt: now });
  if (summary.openCashSessions) push('open-cash-sessions', 'danger', true, 'Hay cajas todavía abiertas', `${summary.openCashSessions} sesiones deben cerrarse o autorizarse antes del cierre.`, 'cash');
  if (summary.cashSessionsWithDifference) push('cash-differences', 'danger', true, 'Existen diferencias de caja', `${summary.cashSessionsWithDifference} cierres tienen sobrante o faltante pendiente de revisión.`, 'cash');
  if (summary.pendingOrders) push('pending-public-orders', 'warning', false, 'Pedidos públicos pendientes', `${summary.pendingOrders} pedidos continúan sin completar.`, 'order');
  if (summary.pendingSpecialOrders) push('pending-special-orders', 'warning', false, 'Apartados o encargos pendientes', `${summary.pendingSpecialOrders} operaciones siguen reservadas, en proceso o parcialmente entregadas.`, 'special-order');
  if (summary.inventoryAdjustments) push('inventory-adjustments', 'warning', false, 'Hubo ajustes de inventario', `${summary.inventoryAdjustments} movimientos requieren revisión en la auditoría.`, 'inventory');
  if (summary.wasteMovements) push('waste-movements', 'warning', false, 'Se registraron mermas o pérdidas', `${summary.wasteMovements} movimientos afectaron las existencias.`, 'inventory');
  const unresolvedCredit = input.receivables.filter((debt) => (!input.branchId || debt.branchId === input.branchId) && debt.balanceCents > 0 && debt.issuedDate <= input.to).length;
  if (unresolvedCredit) push('outstanding-receivables', 'info', false, 'Hay saldos por cobrar', `${unresolvedCredit} cuentas conservan saldo; se muestran separadas del dinero cobrado.`, 'customer');
  if (summary.unsyncedOperations) push('unsynced-operations', 'danger', true, 'Operaciones sin sincronizar', `${summary.unsyncedOperations} cambios siguen pendientes de sincronización.`, 'sync');
  return issues;
}

export const inventoryValue = (catalog: CatalogSnapshot, branchId?: string) => catalog.inventory.filter((row) => !branchId || row.branchId === branchId).reduce((sum, row) => {
  const product = catalog.products.find((item) => item.id === row.productId);
  const variant = row.variantId ? catalog.variants.find((item) => item.id === row.variantId) : undefined;
  return sum + Math.max(0, row.onHand) * (variant?.costCents ?? product?.costCents ?? 0);
}, 0);

export const previousMonthRange = (month: string) => {
  const [year, value] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, value - 2, 1, 12));
  const previous = date.toISOString().slice(0, 7);
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12)).toISOString().slice(0, 10);
  return { from: `${previous}-01`, to: last };
};
